import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MessageCircle, Calendar } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL, WHATSAPP_NUMBER } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", company: "", designation: "", email: "", phone: "", requirement: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-16 pb-12 md:pt-24 border-b border-[#E5E5E5]">
        <div className="container-cc">
          <div className="overline mb-8"><span className="kicker-line" />Contact</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] max-w-5xl">
            Let's start a <span className="italic text-[#A60201]">conversation.</span>
          </h1>
          <p className="mt-8 text-lg text-[#525252] max-w-2xl leading-relaxed">
            Whether you're scoping a multi-year culture program or planning your next foundation day — we'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-cc grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {submitted ? (
              <div className="border border-[#E5E5E5] p-12 bg-white" data-testid="contact-success">
                <div className="overline text-[#A60201] mb-4">Submission received</div>
                <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">Thank you. We'll be in touch within one business day.</h2>
                <p className="mt-5 text-[#525252]">In the meantime, feel free to book a discovery call directly on our calendar.</p>
                <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="btn-primary mt-8" data-testid="success-book-call">Book Discovery Call <ArrowUpRight size={16} /></a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-px bg-[#E5E5E5] border border-[#E5E5E5]" data-testid="lead-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px">
                  <Field label="Full Name" required value={form.name} onChange={set("name")} testid="lead-name" />
                  <Field label="Company" required value={form.company} onChange={set("company")} testid="lead-company" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px">
                  <Field label="Designation" required value={form.designation} onChange={set("designation")} testid="lead-designation" />
                  <Field label="Work Email" required type="email" value={form.email} onChange={set("email")} testid="lead-email" />
                </div>
                <Field label="Phone" required value={form.phone} onChange={set("phone")} testid="lead-phone" />
                <div className="bg-white p-6">
                  <label className="overline block mb-3">Tell us about your requirement</label>
                  <textarea
                    data-testid="lead-requirement"
                    required
                    value={form.requirement}
                    onChange={set("requirement")}
                    rows={5}
                    className="w-full text-base bg-transparent outline-none placeholder:text-[#A1A1A1] resize-none"
                    placeholder="What culture or employee experience challenge would you like to solve?"
                  />
                </div>
                <div className="bg-white p-6 flex items-center justify-between flex-wrap gap-4">
                  <div className="text-xs text-[#525252]">By submitting, you agree to be contacted by the CraftCulture team.</div>
                  <button data-testid="lead-submit-btn" type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                    {loading ? "Sending..." : "Send enquiry"} <ArrowUpRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="lg:col-span-4 lg:col-start-9 space-y-px bg-[#E5E5E5] border border-[#E5E5E5] self-start">
            <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="bg-white p-7 flex items-start gap-4 hover:bg-[#FFE8D9] transition-colors" data-testid="contact-book-call">
              <Calendar size={22} className="text-[#A60201] mt-1" />
              <div>
                <div className="font-serif-display text-xl">Book a Discovery Call</div>
                <div className="text-sm text-[#525252] mt-1">30 minutes, on your calendar.</div>
              </div>
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="bg-white p-7 flex items-start gap-4 hover:bg-[#FFE8D9] transition-colors" data-testid="contact-email-link">
              <Mail size={22} className="text-[#A60201] mt-1" />
              <div>
                <div className="font-serif-display text-xl">Email us</div>
                <div className="text-sm text-[#525252] mt-1 break-all">{CONTACT_EMAIL}</div>
              </div>
            </a>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="bg-white p-7 flex items-start gap-4 hover:bg-[#FFE8D9] transition-colors" data-testid="contact-whatsapp-link">
              <MessageCircle size={22} className="text-[#A60201] mt-1" />
              <div>
                <div className="font-serif-display text-xl">WhatsApp</div>
                <div className="text-sm text-[#525252] mt-1">+91 8005 869 125</div>
              </div>
            </a>
            <div className="bg-[#0A0A0A] text-white p-7">
              <div className="overline text-white/60 mb-3">Offices</div>
              <div className="text-sm space-y-1">
                <div>Bengaluru · India</div>
                <div>Mumbai · India</div>
                <div>Singapore</div>
                <div>Dubai</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="bg-white p-6">
      <label className="overline block mb-3">{label}{required && <span className="text-[#A60201] ml-1">*</span>}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full text-base bg-transparent outline-none placeholder:text-[#A1A1A1]"
      />
    </div>
  );
}
